import { ArrowRight } from "lucide-react";

import { SetupMode } from "@/types/mockups/genie-setup-v2";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface ModeSelectorProps {
  onSelect: (mode: SetupMode) => void;
}

interface ModeCard {
  id: SetupMode;
  genieIcon: GenieIconName;
  label: string;
  recommended?: boolean;
  badge?: string;
  description: string;
  bullets: string[];
  estimate: string;
}

const modes: ModeCard[] = [
  {
    id: "click",
    genieIcon: "clipboard-check",
    label: "Click setup",
    recommended: true,
    description: "Answer a few quick questions. We'll handle the rest.",
    bullets: [
      "Pick your genie's job, voice, and flows",
      "See exactly how it'll respond as you choose",
      "No prompt writing required",
    ],
    estimate: "About 3 minutes",
  },
  {
    id: "type",
    genieIcon: "chat",
    label: "Type setup",
    description: "Describe your genie in your own words. We'll configure it.",
    bullets: [
      "Write a sentence about what you need",
      "We fill in the wizard for you",
      "Tweak anything before you deploy",
    ],
    estimate: "About 2 minutes",
  },
  {
    id: "voice",
    genieIcon: "microphone",
    label: "Voice setup",
    badge: "New",
    description: "Talk to a setup genie. It interviews you and builds it.",
    bullets: [
      "Hands-free, just answer the questions",
      "Great for setting up on the go",
      "Switch to click or type anytime",
    ],
    estimate: "About 4 minutes",
  },
];

export const ModeSelector = ({ onSelect }: ModeSelectorProps) => {
  return (
    <div className="py-6">
      <div className="mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-[#222B45] leading-tight">
          How would you like to set up your genie?
        </h2>
        <p className="text-sm md:text-base text-[#64748B] mt-2 max-w-2xl">
          Three ways in. They all end at the same place — a fully configured genie that behaves
          the way you want.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {modes.map((mode) => (
          <ModeCard key={mode.id} mode={mode} onClick={() => onSelect(mode.id)} />
        ))}
      </div>

      <p className="text-center text-xs text-gray-500 mt-8">
        You can switch modes anytime during setup.
      </p>
    </div>
  );
};

const ModeCard = ({ mode, onClick }: { mode: ModeCard; onClick: () => void }) => {
  const inner = (
    <div className="rounded-[14px] bg-white p-5 md:p-6 h-full flex flex-col">
      <div className="flex items-start justify-between mb-4">
        <GenieIcon name={mode.genieIcon} size={40} className="h-10 w-10" />
        {mode.recommended && (
          <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full bg-genie-primary/10 text-genie-primary">
            Recommended
          </span>
        )}
        {mode.badge && (
          <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full bg-genie-secondary/10 text-genie-secondary">
            {mode.badge}
          </span>
        )}
      </div>

      <h3 className="text-lg font-semibold text-gray-900 mb-2">{mode.label}</h3>
      <p className="text-sm text-gray-600 mb-5">{mode.description}</p>

      <ul className="space-y-2 mb-6 flex-1">
        {mode.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2 text-sm text-gray-700">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-genie-primary shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <span className="text-xs text-gray-500">{mode.estimate}</span>
        <span className="flex items-center gap-1 text-sm font-semibold text-genie-primary">
          Start
          <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </div>
  );

  if (mode.recommended) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="text-left p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-all hover:shadow-xl hover:shadow-genie-primary/20"
      >
        {inner}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-2xl bg-white border-2 border-gray-200 hover:border-genie-primary hover:shadow-lg hover:shadow-genie-primary/10 transition-all"
    >
      {inner}
    </button>
  );
};
